/**
 * Admin Subscribers Page (/admin/subscribers)
 *
 * Full-feature management surface for the public email_subscribers list:
 *   • KPI strip (total / confirmed / new / churn)
 *   • Search + status/source/tag filters + bulk-action toolbar
 *   • Sortable, selectable, paginated table
 *   • Subscriber detail drawer (profile + tags/notes + activity + edit + delete)
 *   • Compose email modal — individual send, broadcast to filtered set, test send
 *
 * Backed by /api/admin/subscribers/* (see subscribers-admin-workflow.ts).
 * Admin role is enforced on both the route element (RequireAdmin) and every
 * underlying API call.
 */
import {
  ArrowLeft,
  Download,
  Loader2,
  Mail,
  Megaphone,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  UserCheck,
  Users,
  Wand2,
  X,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SubscriberSignupChart } from "@/components/analytics/SubscriberSignupChart";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/Modal";
import {
  useAiDraftEmail,
  useBroadcastPreview,
  useBroadcastSend,
  useBulkSubscriberAction,
  useDeleteSubscriber,
  useSendIndividualEmail,
  useSubscriberDetail,
  useSubscribersList,
  useSubscribersStats,
  useUpdateSubscriber,
  buildCsvExportUrl,
  type AdminSubscriber,
  type BroadcastPreviewResponse,
  type ListFilters,
  type SignupTimeseriesRange,
  type SubscriberKind,
  type SubscriberStatus,
} from "@/hooks/useSubscribersAdmin";
import { notify } from "@/utils/notifications";

type SortDir = "asc" | "desc";

const STATUS_COLORS: Record<string, string> = {
  // Email-list statuses
  confirmed: "bg-green-100 text-green-700 border-green-300",
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  unsubscribed: "bg-slate-100 text-slate-600 border-slate-300",
  bounced: "bg-red-100 text-red-700 border-red-300",
  complained: "bg-red-200 text-red-800 border-red-400",
  // Paid (Stripe) statuses
  active: "bg-green-100 text-green-700 border-green-300",
  trialing: "bg-blue-100 text-blue-700 border-blue-300",
  past_due: "bg-orange-100 text-orange-700 border-orange-300",
  canceled: "bg-slate-100 text-slate-600 border-slate-300",
  incomplete: "bg-amber-100 text-amber-700 border-amber-300",
};

const DEFAULT_PAGE_SIZE = 50;

// Sends are slower + higher-stakes than other actions; extend their toasts
// so the admin (and QA / automation) has a real chance to see the result
// before it auto-dismisses. Default toast duration is 4s.
const SEND_TOAST_MS = 7000;

const TEMPLATE_TOKENS: Array<{ token: string; label: string }> = [
  { token: "{{first_name}}", label: "First name" },
  { token: "{{last_name}}", label: "Last name" },
  { token: "{{email}}", label: "Email" },
  { token: "{{company}}", label: "Company" },
  { token: "{{unsubscribe_url}}", label: "Unsubscribe link" },
];

export default function SubscribersAdminPage() {
  const navigate = useNavigate();

  // ── Filter / sort / pagination state ─────────────────────────────────
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState<SubscriberStatus | string | "all">(
    "all",
  );
  const [source, setSource] = useState<string>("");
  const [tag, setTag] = useState<string>("");
  const [hasName, setHasName] = useState(false);
  const [type, setType] = useState<SubscriberKind | "all">("all");
  const [chartRange, setChartRange] = useState<SignupTimeseriesRange>("month");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Debounce search input.
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const filters: ListFilters = useMemo(
    () => ({
      q: debouncedQ || undefined,
      status,
      source: source || undefined,
      tag: tag || undefined,
      hasName: hasName || undefined,
      type,
    }),
    [debouncedQ, status, source, tag, hasName, type],
  );

  // ── Data ─────────────────────────────────────────────────────────────
  const statsQ = useSubscribersStats();
  const listQ = useSubscribersList({
    ...filters,
    page,
    pageSize,
    sortBy,
    sortDir,
  });

  // ── Selection ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allOnPage = listQ.data?.rows ?? [];
  const allSelectedOnPage =
    allOnPage.length > 0 && allOnPage.every((r) => selected.has(r.id));
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        for (const r of allOnPage) next.delete(r.id);
      } else {
        for (const r of allOnPage) next.add(r.id);
      }
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Mutations ────────────────────────────────────────────────────────
  const bulkMutation = useBulkSubscriberAction();

  // ── UI: detail drawer + compose modal state ──────────────────────────
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const [composeMode, setComposeMode] = useState<
    "individual" | "broadcast" | null
  >(null);
  const [composeTargetId, setComposeTargetId] = useState<string | null>(null);

  // ── Bulk-action toolbar UI state ─────────────────────────────────────
  // Inline tag-input mode replaces window.prompt() (which is auto-dismissed
  // by some browser automation and blocked in sandboxed iframes). When the
  // admin clicks "Add tag", we swap the toolbar into a row with an input
  // + Apply + Cancel. The bulkTagInputRef is used to auto-focus on open.
  const [bulkTagInputMode, setBulkTagInputMode] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const bulkTagInputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (bulkTagInputMode) {
      // Defer to next tick so the input has actually mounted.
      const t = window.setTimeout(() => bulkTagInputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [bulkTagInputMode]);

  // Two-click confirm for bulk Delete — same pattern as the Discard draft
  // link in the compose modal. First click arms (red button, label flips);
  // a second click within BULK_DELETE_ARM_MS performs the destructive
  // delete. Auto-disarms on timeout or when the selection changes (e.g.
  // user clears selection or switches tabs).
  const [bulkDeleteArmed, setBulkDeleteArmed] = useState(false);
  const bulkDeleteTimerRef = React.useRef<number | null>(null);
  const BULK_DELETE_ARM_MS = 4000;
  React.useEffect(() => {
    // Disarm whenever the underlying selection changes — if the admin
    // changes who they're about to delete mid-arm, that should require
    // a fresh confirm against the new set.
    setBulkDeleteArmed(false);
    if (bulkDeleteTimerRef.current !== null) {
      window.clearTimeout(bulkDeleteTimerRef.current);
      bulkDeleteTimerRef.current = null;
    }
  }, [selected]);
  React.useEffect(() => {
    return () => {
      if (bulkDeleteTimerRef.current !== null) {
        window.clearTimeout(bulkDeleteTimerRef.current);
      }
    };
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  // Applies the tag the admin typed into the inline input. Called both by
  // the Apply button and the Enter key. Dismisses the input row on success.
  const handleBulkApplyTag = async () => {
    const tag = bulkTagInput.trim();
    if (!tag) {
      notify.error("Enter a tag name.");
      return;
    }
    const count = selected.size;
    await bulkMutation.mutateAsync({
      ids: Array.from(selected),
      action: "add_tag",
      tag,
    });
    notify.success(`Tagged ${count} subscriber(s) with "${tag}".`);
    setBulkTagInput("");
    setBulkTagInputMode(false);
    setSelected(new Set());
  };

  // Status changes are reversible (the admin can always flip back), so no
  // confirm dialog — apply directly. Toast confirms the change.
  const handleBulkSetStatus = async (status: SubscriberStatus) => {
    const count = selected.size;
    await bulkMutation.mutateAsync({
      ids: Array.from(selected),
      action: "set_status",
      status,
    });
    notify.success(`Updated ${count} subscriber(s) to "${status}".`);
    setSelected(new Set());
  };

  // Two-click confirm for delete. First click arms; second click within
  // BULK_DELETE_ARM_MS performs the irreversible delete.
  const handleBulkDelete = async () => {
    if (!bulkDeleteArmed) {
      setBulkDeleteArmed(true);
      if (bulkDeleteTimerRef.current !== null) {
        window.clearTimeout(bulkDeleteTimerRef.current);
      }
      bulkDeleteTimerRef.current = window.setTimeout(() => {
        setBulkDeleteArmed(false);
        bulkDeleteTimerRef.current = null;
      }, BULK_DELETE_ARM_MS);
      return;
    }
    // Armed — perform the delete.
    if (bulkDeleteTimerRef.current !== null) {
      window.clearTimeout(bulkDeleteTimerRef.current);
      bulkDeleteTimerRef.current = null;
    }
    setBulkDeleteArmed(false);
    const count = selected.size;
    await bulkMutation.mutateAsync({
      ids: Array.from(selected),
      action: "delete",
    });
    notify.success(`Deleted ${count} subscriber(s).`);
    setSelected(new Set());
  };

  const handleExportCsv = () => {
    const url = buildCsvExportUrl(filters);
    // Open in same tab so the session cookie is forwarded. The browser
    // sees Content-Disposition: attachment from the backend and saves
    // the file rather than navigating. Fire a toast immediately so the
    // admin has clear confirmation the click registered — the download
    // itself is invisible until it lands in the OS download tray.
    notify.success("Subscribers exported. Check your downloads.");
    window.location.href = url;
  };

  const handleRefresh = () => {
    statsQ.refetch();
    listQ.refetch();
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin/analytics")}
            className="flex items-center gap-2 text-slate-600 hover:text-[#B87333] mb-6 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back to Admin</span>
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-black text-slate-900">
                  Subscribers
                </h1>
                <Badge
                  variant="outline"
                  className="bg-purple-100 text-purple-700 border-purple-300"
                >
                  Admin
                </Badge>
              </div>
              <p className="text-lg text-slate-600">
                Manage your email list — edit profiles, segment with tags, and
                send announcements.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={statsQ.isFetching || listQ.isFetching}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${statsQ.isFetching || listQ.isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleExportCsv}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button
                onClick={() => {
                  setComposeTargetId(null);
                  setComposeMode("broadcast");
                }}
                className="bg-[#B87333] hover:bg-[#A67329] flex items-center gap-2"
              >
                <Megaphone className="h-4 w-4" />
                Broadcast
              </Button>
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <KpiStrip loading={statsQ.isLoading} stats={statsQ.data} />

        {/* Type tabs — switch between email list, paid subscribers, or both */}
        <div className="flex gap-2 mb-4">
          {(
            [
              { value: "all", label: "All", hint: listQ.data?.total },
              {
                value: "email",
                label: "Email list",
                hint: listQ.data?.counts?.email,
              },
              {
                value: "paid",
                label: "Paid subscribers",
                hint: listQ.data?.counts?.paid,
              },
            ] as const
          ).map((tab) => (
            <Button
              key={tab.value}
              variant={type === tab.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setType(tab.value);
                setPage(1);
                setSelected(new Set());
              }}
              className={
                type === tab.value ? "bg-[#B87333] hover:bg-[#A67329]" : ""
              }
            >
              {tab.label}
              {typeof tab.hint === "number" && (
                <span className="ml-2 text-xs opacity-70">
                  ({tab.hint.toLocaleString()})
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Signup trend chart — driven by the active type tab */}
        <SubscriberSignupChart
          type={type}
          range={chartRange}
          onRangeChange={setChartRange}
        />

        {/* Filter bar */}
        <Card className="mb-6">
          <CardContent className="py-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[260px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search email, name, or company..."
                  className="pl-9"
                />
              </div>
              <Select
                value={status}
                onChange={(v) => {
                  setStatus(v as any);
                  setPage(1);
                }}
                options={[
                  { value: "all", label: "All statuses" },
                  ...(listQ.data?.facets.statuses.map((s) => ({
                    value: s.status,
                    label: `${s.status} (${s.count})`,
                  })) ?? []),
                ]}
              />
              <Select
                value={source}
                onChange={(v) => {
                  setSource(v);
                  setPage(1);
                }}
                options={[
                  { value: "", label: "All sources" },
                  ...(listQ.data?.facets.sources.map((s) => ({
                    value: s.source,
                    label: `${s.source} (${s.count})`,
                  })) ?? []),
                ]}
              />
              <Select
                value={tag}
                onChange={(v) => {
                  setTag(v);
                  setPage(1);
                }}
                options={[
                  { value: "", label: "All tags" },
                  ...(listQ.data?.facets.tags.map((t) => ({
                    value: t.tag,
                    label: `${t.tag} (${t.count})`,
                  })) ?? []),
                ]}
              />
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={hasName}
                  onCheckedChange={(c) => {
                    setHasName(!!c);
                    setPage(1);
                  }}
                />
                Has name on file
              </label>
              {(debouncedQ || status !== "all" || source || tag || hasName) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setQ("");
                    setStatus("all");
                    setSource("");
                    setTag("");
                    setHasName(false);
                  }}
                  className="text-slate-600"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Bulk action toolbar. Renders one of two layouts: the inline
                tag-input row when the admin clicked "Add tag" (replaces
                window.prompt), or the standard action row otherwise. */}
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-purple-900">
                  {selected.size} selected
                </span>
                <div className="h-4 w-px bg-purple-200 mx-1" />

                {bulkTagInputMode ? (
                  <>
                    <Input
                      ref={bulkTagInputRef}
                      value={bulkTagInput}
                      onChange={(e) => setBulkTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void handleBulkApplyTag();
                        } else if (e.key === "Escape") {
                          setBulkTagInputMode(false);
                          setBulkTagInput("");
                        }
                      }}
                      placeholder="Tag name (e.g. vip, beta)"
                      className="h-8 text-sm w-56"
                      maxLength={64}
                      disabled={bulkMutation.isPending}
                    />
                    <Button
                      size="sm"
                      onClick={handleBulkApplyTag}
                      disabled={bulkMutation.isPending || !bulkTagInput.trim()}
                      className="bg-[#B87333] hover:bg-[#A67329]"
                    >
                      {bulkMutation.isPending ? "Applying…" : "Apply"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setBulkTagInputMode(false);
                        setBulkTagInput("");
                      }}
                      disabled={bulkMutation.isPending}
                      className="text-slate-600"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkTagInputMode(true)}
                      disabled={bulkMutation.isPending}
                    >
                      Add tag
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkSetStatus("confirmed")}
                      disabled={bulkMutation.isPending}
                    >
                      Mark confirmed
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkSetStatus("unsubscribed")}
                      disabled={bulkMutation.isPending}
                    >
                      Unsubscribe
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setComposeTargetId(null);
                        setComposeMode("broadcast");
                      }}
                      className="text-purple-700 border-purple-300"
                    >
                      <Mail className="h-3 w-3 mr-1" /> Email selected
                    </Button>
                    {/* Delete uses the armed two-click pattern. When armed,
                        the button turns solid red, the label flips to the
                        recipient count, and a second click within 4s fires. */}
                    <Button
                      size="sm"
                      variant={bulkDeleteArmed ? "default" : "outline"}
                      onClick={handleBulkDelete}
                      disabled={bulkMutation.isPending}
                      className={
                        bulkDeleteArmed
                          ? "bg-red-600 hover:bg-red-700 text-white font-semibold"
                          : "text-red-600 border-red-300 hover:bg-red-50"
                      }
                      aria-pressed={bulkDeleteArmed}
                      title={
                        bulkDeleteArmed
                          ? "Click again within 4s to confirm permanent delete."
                          : `Permanently delete the ${selected.size} selected subscriber(s).`
                      }
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      {bulkDeleteArmed
                        ? `Click again to delete ${selected.size}`
                        : "Delete"}
                    </Button>
                  </>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelected(new Set());
                    setBulkTagInputMode(false);
                    setBulkTagInput("");
                  }}
                  className="ml-auto text-slate-600"
                >
                  Clear selection
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>
                Subscribers
                {listQ.data && (
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    ({listQ.data.total.toLocaleString()} match)
                  </span>
                )}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {listQ.isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
              </div>
            ) : listQ.data && listQ.data.rows.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                No subscribers match your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <Checkbox
                          checked={allSelectedOnPage}
                          onCheckedChange={toggleAllOnPage}
                        />
                      </th>
                      <Th
                        label="Email"
                        col="email"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th className="px-4 py-3">Name</th>
                      <Th
                        label="Status"
                        col="status"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Tags</th>
                      <Th
                        label="Subscribed"
                        col="created_at"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th className="px-4 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {listQ.data?.rows.map((row) => (
                      <SubscriberRow
                        key={row.id}
                        row={row}
                        selected={selected.has(row.id)}
                        onToggle={() => toggleOne(row.id)}
                        onOpenDetail={() => setOpenDetailId(row.id)}
                        onSendEmail={() => {
                          setComposeTargetId(row.id);
                          setComposeMode("individual");
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {listQ.data && listQ.data.total > pageSize && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={listQ.data.total}
            onChange={setPage}
          />
        )}
      </div>

      {/* Detail drawer */}
      {openDetailId && (
        <DetailDrawer
          id={openDetailId}
          onClose={() => setOpenDetailId(null)}
          onCompose={() => {
            setComposeTargetId(openDetailId);
            setComposeMode("individual");
          }}
          onDeleted={() => {
            setOpenDetailId(null);
            listQ.refetch();
            statsQ.refetch();
          }}
        />
      )}

      {/* Compose modal */}
      {composeMode && (
        <ComposeModal
          mode={composeMode}
          targetId={composeTargetId}
          selectedIds={Array.from(selected)}
          filters={filters}
          onClose={() => setComposeMode(null)}
          onSent={() => {
            listQ.refetch();
            statsQ.refetch();
          }}
        />
      )}
    </div>
  );
}

// ── KPI strip ────────────────────────────────────────────────────────────
function KpiStrip({
  loading,
  stats,
}: {
  loading: boolean;
  stats?: ReturnType<typeof useSubscribersStats>["data"];
}) {
  const items = [
    {
      label: "Email list",
      value: stats?.total ?? 0,
      sub: `${stats?.confirmed ?? 0} confirmed · ${stats?.new_7d ?? 0} this week`,
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: "Paid subscribers",
      value: (stats?.paid?.active ?? 0) + (stats?.paid?.trialing ?? 0),
      sub: `${stats?.paid?.active ?? 0} active · ${stats?.paid?.trialing ?? 0} trialing`,
      icon: <UserCheck className="h-5 w-5 text-green-600" />,
    },
    {
      label: "New (30d)",
      value: (stats?.new_30d ?? 0) + (stats?.paid?.new_30d ?? 0),
      sub: `${stats?.new_30d ?? 0} email · ${stats?.paid?.new_30d ?? 0} paid`,
      icon: <Users className="h-5 w-5 text-blue-600" />,
    },
    {
      label: "Churn (30d)",
      value: (stats?.unsubscribed_30d ?? 0) + (stats?.paid?.canceled_30d ?? 0),
      sub: `${stats?.unsubscribed_30d ?? 0} unsub · ${stats?.paid?.canceled_30d ?? 0} canceled · ${stats?.bounced ?? 0} bounced`,
      icon: <X className="h-5 w-5 text-red-500" />,
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {items.map((it, i) => (
        <Card key={i} className="border-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-600">
              {it.label}
            </CardTitle>
            <div className="p-2 bg-slate-100 rounded-lg">{it.icon}</div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">
              {loading ? "—" : it.value.toLocaleString()}
            </div>
            {it.sub && <p className="text-xs text-slate-500 mt-1">{it.sub}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Table row ────────────────────────────────────────────────────────────
function SubscriberRow({
  row,
  selected,
  onToggle,
  onOpenDetail,
  onSendEmail,
}: {
  row: AdminSubscriber;
  selected: boolean;
  onToggle: () => void;
  onOpenDetail: () => void;
  onSendEmail: () => void;
}) {
  const fullName = [row.first_name, row.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const cls = STATUS_COLORS[row.status] || "bg-slate-100 text-slate-700";
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-4 py-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
      </td>
      <td className="px-4 py-3">
        <button
          onClick={onOpenDetail}
          className="font-medium text-slate-900 hover:text-[#B87333] text-left"
        >
          {row.email}
        </button>
      </td>
      <td className="px-4 py-3 text-slate-700">
        {fullName || <span className="text-slate-400">—</span>}
        {row.company && (
          <div className="text-xs text-slate-500">{row.company}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <Badge variant="outline" className={cls}>
            {row.status}
          </Badge>
          {row.kind === "paid" && row.subscription?.plan_name && (
            <span className="text-xs text-slate-500">
              {row.subscription.plan_name}
              {row.subscription.billing_cycle &&
                ` · ${row.subscription.billing_cycle}`}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {row.kind === "paid" ? (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200"
          >
            paid
          </Badge>
        ) : (
          row.source
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {row.tags.slice(0, 3).map((t) => (
            <Badge
              key={t}
              variant="outline"
              className="bg-purple-50 text-purple-700 border-purple-200 text-xs"
            >
              {t}
            </Badge>
          ))}
          {row.tags.length > 3 && (
            <span className="text-xs text-slate-500">
              +{row.tags.length - 3}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {formatDate(row.created_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          size="sm"
          variant="ghost"
          onClick={onSendEmail}
          disabled={[
            "unsubscribed",
            "bounced",
            "complained",
            "canceled",
            "incomplete_expired",
          ].includes(row.status)}
          title="Send email"
        >
          <Mail className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function Th({
  label,
  col,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  col: string;
  sortBy: string;
  sortDir: SortDir;
  onSort: (c: string) => void;
}) {
  const active = sortBy === col;
  return (
    <th className="px-4 py-3">
      <button
        onClick={() => onSort(col)}
        className={`flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900 font-semibold" : ""}`}
      >
        {label}
        {active && (
          <span className="text-xs">{sortDir === "asc" ? "▲" : "▼"}</span>
        )}
      </button>
    </th>
  );
}

// ── Pagination ──────────────────────────────────────────────────────────
function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-sm text-slate-600">
        Page {page} of {lastPage} · {total.toLocaleString()} subscribers
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= lastPage}
          onClick={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// ── Select (searchable dropdown wrapper) ──────────────────────────────
// Thin shim so callers in this file keep their compact prop signature;
// the actual UI is the project-wide SearchableSelect.
function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="min-w-[10rem]">
      <SearchableSelect
        value={value}
        onChange={(next) => onChange(next ?? options[0]?.value ?? "")}
        options={options}
        placeholder="—"
        searchPlaceholder="Search…"
      />
    </div>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────────
function DetailDrawer({
  id,
  onClose,
  onCompose,
  onDeleted,
}: {
  id: string;
  onClose: () => void;
  onCompose: () => void;
  onDeleted: () => void;
}) {
  const { data, isLoading } = useSubscriberDetail(id);
  const updateMutation = useUpdateSubscriber();
  const deleteMutation = useDeleteSubscriber();

  // Local editable copy.
  const [draft, setDraft] = useState<{
    first_name: string;
    last_name: string;
    company: string;
    status: SubscriberStatus | "pending";
    tags: string;
    notes: string;
  } | null>(null);

  React.useEffect(() => {
    if (!data) return;
    setDraft({
      first_name: data.first_name ?? "",
      last_name: data.last_name ?? "",
      company: data.company ?? "",
      status: (data.status as SubscriberStatus) ?? "pending",
      tags: data.tags.join(", "),
      notes: data.notes,
    });
  }, [data]);

  const handleSave = async () => {
    if (!draft || !data) return;
    await updateMutation.mutateAsync({
      id,
      payload: {
        first_name: draft.first_name.trim() || null,
        last_name: draft.last_name.trim() || null,
        company: draft.company.trim() || null,
        status: draft.status as SubscriberStatus,
        tags: draft.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        notes: draft.notes,
      },
    });
    notify.success("Subscriber updated.");
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Permanently delete this subscriber? This cannot be undone.",
      )
    )
      return;
    await deleteMutation.mutateAsync(id);
    notify.success("Subscriber deleted.");
    onDeleted();
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">
            Subscriber details
          </h2>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        {isLoading || !data || !draft ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#B87333]" />
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {data.email}
                </div>
                {data.kind === "paid" && (
                  <Badge
                    variant="outline"
                    className="bg-purple-50 text-purple-700 border-purple-200"
                  >
                    Paid subscriber
                  </Badge>
                )}
              </div>
              <div className="text-sm text-slate-500">
                Subscribed {formatDateTime(data.created_at)} via{" "}
                <span className="font-mono">{data.source}</span>
              </div>
              <Badge
                variant="outline"
                className={
                  "mt-2 " +
                  (STATUS_COLORS[data.status] ?? "bg-slate-100 text-slate-700")
                }
              >
                {data.status}
              </Badge>
              {data.kind === "paid" && data.subscription && (
                <div className="mt-3 text-sm text-slate-700 space-y-1">
                  {data.subscription.plan_name && (
                    <div>
                      <span className="text-slate-500">Plan:</span>{" "}
                      {data.subscription.plan_name}
                      {data.subscription.billing_cycle &&
                        ` (${data.subscription.billing_cycle})`}
                    </div>
                  )}
                  {data.subscription.current_period_end && (
                    <div>
                      <span className="text-slate-500">Renews:</span>{" "}
                      {formatDateTime(data.subscription.current_period_end)}
                    </div>
                  )}
                  {data.subscription.trial_end && (
                    <div>
                      <span className="text-slate-500">Trial ends:</span>{" "}
                      {formatDateTime(data.subscription.trial_end)}
                    </div>
                  )}
                  {/* Always render the Last payment row for any paid
                      subscriber so the Stripe block stays structurally
                      consistent across active / trialing / past_due rows.
                      Show "—" with a contextual hint when no payment is
                      on file (almost always: brand-new trial). */}
                  <div>
                    <span className="text-slate-500">Last payment:</span>{" "}
                    {data.subscription.last_payment_amount != null ? (
                      <>
                        ${data.subscription.last_payment_amount.toFixed(2)}
                        {data.subscription.last_payment_date &&
                          ` on ${formatDate(data.subscription.last_payment_date)}`}
                      </>
                    ) : (
                      <span className="text-slate-400">
                        —{" "}
                        <span className="text-xs">
                          {data.status === "trialing"
                            ? "(trial — no payment yet)"
                            : "(no payments on file)"}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Activity */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Activity</CardTitle>
              </CardHeader>
              <CardContent className="pt-2 grid grid-cols-3 gap-2 text-center">
                <Stat label="Enrollments" value={data.activity.enrollments} />
                <Stat label="Event RSVPs" value={data.activity.event_rsvps} />
                <Stat label="AI chats" value={data.activity.ai_conversations} />
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Email engagement</CardTitle>
              </CardHeader>
              <CardContent className="pt-2 text-sm text-slate-700 space-y-1">
                <div>
                  <span className="text-slate-500">Last emailed:</span>{" "}
                  {data.last_emailed_at
                    ? formatDateTime(data.last_emailed_at)
                    : "Never"}
                </div>
                <div>
                  <span className="text-slate-500">Total sends:</span>{" "}
                  {data.total_emails_sent}
                </div>
                {data.confirmed_at && (
                  <div>
                    <span className="text-slate-500">Confirmed:</span>{" "}
                    {formatDateTime(data.confirmed_at)}
                  </div>
                )}
                {data.unsubscribed_at && (
                  <div>
                    <span className="text-slate-500">Unsubscribed:</span>{" "}
                    {formatDateTime(data.unsubscribed_at)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit form (email-list rows only — paid subscribers are
                managed via Stripe / the billing portal) */}
            {data.kind === "paid" ? (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600">
                Paid subscribers are managed via Stripe. You can email this
                customer below, but plan/status changes belong in the billing
                portal.
              </div>
            ) : (
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Edit profile</h3>
                <Field label="First name">
                  <Input
                    value={draft.first_name}
                    onChange={(e) =>
                      setDraft({ ...draft, first_name: e.target.value })
                    }
                  />
                </Field>
                <Field label="Last name">
                  <Input
                    value={draft.last_name}
                    onChange={(e) =>
                      setDraft({ ...draft, last_name: e.target.value })
                    }
                  />
                </Field>
                <Field label="Company">
                  <Input
                    value={draft.company}
                    onChange={(e) =>
                      setDraft({ ...draft, company: e.target.value })
                    }
                  />
                </Field>
                <Field label="Status">
                  <Select
                    value={draft.status}
                    onChange={(v) =>
                      setDraft({
                        ...draft,
                        status: v as SubscriberStatus,
                      })
                    }
                    options={[
                      { value: "pending", label: "pending" },
                      { value: "confirmed", label: "confirmed" },
                      { value: "unsubscribed", label: "unsubscribed" },
                      { value: "bounced", label: "bounced" },
                      { value: "complained", label: "complained" },
                    ]}
                  />
                </Field>
                <Field
                  label="Tags"
                  hint="Comma-separated. Use for segmenting broadcasts."
                >
                  <Input
                    value={draft.tags}
                    onChange={(e) =>
                      setDraft({ ...draft, tags: e.target.value })
                    }
                    placeholder="vip, beta, early-access"
                  />
                </Field>
                <Field label="Admin notes (private)">
                  <textarea
                    value={draft.notes}
                    onChange={(e) =>
                      setDraft({ ...draft, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full rounded-md border border-slate-300 bg-white text-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B87333]/50"
                  />
                </Field>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t">
              {data.kind === "email" && (
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="bg-[#B87333] hover:bg-[#A67329]"
                >
                  {updateMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onCompose}
                disabled={[
                  "unsubscribed",
                  "bounced",
                  "complained",
                  "canceled",
                  "incomplete_expired",
                ].includes(data.status)}
              >
                <Mail className="h-4 w-4 mr-1" />
                Send email
              </Button>
              {data.kind === "email" && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="ml-auto text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-2xl font-bold text-slate-900">
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

// ── Compose modal ───────────────────────────────────────────────────────
function ComposeModal({
  mode,
  targetId,
  selectedIds,
  filters,
  onClose,
  onSent,
}: {
  mode: "individual" | "broadcast";
  targetId: string | null;
  selectedIds: string[];
  filters: ListFilters;
  onClose: () => void;
  onSent: () => void;
}) {
  const targetDetailQ = useSubscriberDetail(
    mode === "individual" ? targetId : null,
  );
  const sendIndividual = useSendIndividualEmail();
  const previewBroadcast = useBroadcastPreview();
  const sendBroadcast = useBroadcastSend();
  const aiDraft = useAiDraftEmail();

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(
    "<p>Hi {{first_name}},</p>\n<p></p>\n<p>— Noah</p>",
  );
  const [fromName, setFromName] = useState("AI Genius Automations");
  const [testRecipient, setTestRecipient] = useState("");
  const [preview, setPreview] = useState<BroadcastPreviewResponse | null>(null);

  // ── AI helper state ──
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiAudience, setAiAudience] = useState("");
  // Tracks which quick-prompt button is currently driving an in-flight AI
  // request so we can render a per-button spinner instead of a vague "the
  // whole panel is loading" state. Cleared after the draft resolves.
  const [activeQuickPrompt, setActiveQuickPrompt] = useState<string | null>(
    null,
  );
  // {{tokens}} the AI emitted that aren't in our documented merge set —
  // surfaced as a warning in the body Preview tab so the admin can fix
  // them before sending. Cleared whenever the admin edits the body
  // manually (a fix might already be in flight).
  const [unknownTokens, setUnknownTokens] = useState<string[]>([]);
  // Two-click confirm for Discard draft. First click arms it (turns red +
  // changes label); a second click within DISCARD_ARM_MS actually discards.
  // Auto-disarms after the window expires so a stale armed state doesn't
  // bite the admin minutes later. Replaces window.confirm() because the
  // native dialog is auto-dismissed by some browser automation tools and
  // can be blocked in sandboxed iframes.
  const [discardArmed, setDiscardArmed] = useState(false);
  const discardTimerRef = React.useRef<number | null>(null);
  const DISCARD_ARM_MS = 4000;
  // Clear any pending arm-timer on unmount so we don't update state after
  // the modal closes.
  React.useEffect(() => {
    return () => {
      if (discardTimerRef.current !== null) {
        window.clearTimeout(discardTimerRef.current);
      }
    };
  }, []);

  const QUICK_PROMPTS: Array<{ label: string; prompt: string }> = [
    {
      label: "Announce new course",
      prompt:
        "Write a short broadcast announcing a new course launch with a friendly tone and a clear CTA to courses.aigeniusautomations.com.",
    },
    {
      label: "Monthly update",
      prompt:
        "Write a monthly update covering 2-3 short highlights, then invite replies with feedback.",
    },
    {
      label: "Re-engage inactive",
      prompt:
        "Write a short, warm re-engagement email for subscribers who haven't opened recent emails. Offer one clear reason to come back.",
    },
    {
      label: "Make it shorter",
      prompt:
        "Cut the current draft to 40% of its length without losing the CTA.",
    },
    {
      label: "More casual tone",
      prompt:
        "Rewrite the current draft in a more casual, conversational tone. Keep the structure and the CTA.",
    },
    {
      label: "Fix HTML",
      prompt:
        "Clean up the HTML of the current draft — fix broken tags, ensure paragraphs are wrapped, remove inline styles.",
    },
  ];

  const handleAiDraft = async (overridePrompt?: string) => {
    const promptToSend = (overridePrompt ?? aiPrompt).trim();
    if (!promptToSend) {
      notify.error("Describe what you want the email to say.");
      return;
    }
    // Snapshot whether this is a "from scratch" generation vs a refine of
    // existing content BEFORE the await — that way the success toast
    // accurately reflects the user's intent at click time.
    const wasRefine = !!subject || html.length > 50;
    try {
      const res = await aiDraft.mutateAsync({
        prompt: promptToSend,
        currentSubject: subject || undefined,
        currentHtml: html || undefined,
        audience:
          aiAudience ||
          (mode === "individual" && targetDetailQ.data
            ? `One subscriber: ${targetDetailQ.data.email}${
                targetDetailQ.data.kind === "paid" ? " (paid customer)" : ""
              }`
            : recipientSummary),
      });
      if (res.success && res.subject && res.html) {
        setSubject(res.subject);
        setHtml(res.html);
        setUnknownTokens(res.unknownTokens ?? []);
        notify.success(wasRefine ? "Draft refined." : "Draft generated.");
        if (res.unknownTokens && res.unknownTokens.length > 0) {
          notify.success(
            `Heads up: ${res.unknownTokens.length} unknown merge token${
              res.unknownTokens.length === 1 ? "" : "s"
            } in draft — see Preview.`,
          );
        }
      } else {
        notify.error(res.message || "AI draft failed.");
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "AI draft failed.");
    }
  };

  // Effective broadcast target: explicit selected ids if any, else the
  // current filter set.
  const broadcastUseSelectedIds = selectedIds.length > 0;

  const recipientSummary =
    mode === "individual"
      ? (targetDetailQ.data?.email ?? "loading...")
      : broadcastUseSelectedIds
        ? `${selectedIds.length} selected`
        : "Filtered list";

  const handlePreview = async () => {
    const res = await previewBroadcast.mutateAsync(
      broadcastUseSelectedIds ? { ids: selectedIds } : { filters },
    );
    setPreview(res);
  };

  const handleSendBroadcast = async () => {
    if (!preview) {
      notify.error("Run a preview first.");
      return;
    }
    if (
      preview.requiresConfirm &&
      !window.confirm(
        `Send this broadcast to ${preview.recipientCount.toLocaleString()} subscriber(s)?`,
      )
    ) {
      return;
    }
    try {
      const res = await sendBroadcast.mutateAsync({
        subject,
        html,
        fromName,
        ...(broadcastUseSelectedIds ? { ids: selectedIds } : { filters }),
        confirmToken: preview.confirmToken,
      });
      if (res.success) {
        notify.success(
          `Broadcast sent: ${res.sent ?? 0} delivered, ${res.failed ?? 0} failed.`,
          { duration: SEND_TOAST_MS },
        );
        onSent();
        onClose();
      } else {
        notify.error(res.message || "Broadcast failed.", {
          duration: SEND_TOAST_MS,
        });
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Broadcast failed.", {
        duration: SEND_TOAST_MS,
      });
    }
  };

  // Local flag so the "Send test" button can show its own loading label
  // separately from the main "Send broadcast" button — both share the
  // sendBroadcast mutation so sendBroadcast.isPending alone can't
  // distinguish which action is in flight.
  const [testSending, setTestSending] = useState(false);
  const handleSendTest = async () => {
    if (!testRecipient) {
      notify.error("Enter a test recipient email.");
      return;
    }
    setTestSending(true);
    try {
      const res = await sendBroadcast.mutateAsync({
        subject,
        html,
        fromName,
        testRecipient,
      });
      if (res.success) {
        notify.success(`Test email sent to ${testRecipient}.`, {
          duration: SEND_TOAST_MS,
        });
      } else {
        notify.error(res.message || "Test send failed.", {
          duration: SEND_TOAST_MS,
        });
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Test send failed.", {
        duration: SEND_TOAST_MS,
      });
    } finally {
      setTestSending(false);
    }
  };

  const handleSendIndividual = async () => {
    if (!targetId) return;
    try {
      const res = await sendIndividual.mutateAsync({
        id: targetId,
        subject,
        html,
        fromName,
      });
      if (res.success) {
        notify.success(res.message, { duration: SEND_TOAST_MS });
        onSent();
        onClose();
      } else {
        notify.error(res.message, { duration: SEND_TOAST_MS });
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Send failed.", {
        duration: SEND_TOAST_MS,
      });
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={mode === "individual" ? "Send email" : "Broadcast email"}
      // xlarge = 1200px max-width. Lets the two-column layout (composer
      // on the left, AI helper on the right) breathe without the textarea
      // being cramped.
      size="xlarge"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-6">
        {/* ── Left column: composer ── */}
        <div className="space-y-4 min-w-0">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
            <div className="font-semibold text-slate-700">To:</div>
            <div className="text-slate-900">{recipientSummary}</div>
            {mode === "broadcast" && (
              <p className="text-xs text-slate-500 mt-1">
                Unsubscribed, bounced, and complained subscribers are always
                excluded automatically. An unsubscribe footer is appended if
                your HTML doesn&apos;t include{" "}
                <code>{`{{unsubscribe_url}}`}</code>.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="From name (display only)">
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="AI Genius Automations"
              />
            </Field>
            <Field label="Subject">
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Your subject line"
              />
            </Field>
          </div>

          <BodyEditor
            html={html}
            onChange={(next) => {
              setHtml(next);
              // Manual edits invalidate the AI-warning list — the admin
              // may already be fixing the offending token. We'll re-derive
              // the warning if/when a fresh AI draft comes back.
              if (unknownTokens.length > 0) setUnknownTokens([]);
            }}
            unknownTokens={unknownTokens}
          />

          <div className="flex flex-wrap gap-2">
            {TEMPLATE_TOKENS.map((t) => (
              <Button
                key={t.token}
                variant="outline"
                size="sm"
                onClick={() => setHtml((s) => s + " " + t.token)}
                className="text-xs"
              >
                {t.label}
                <code className="ml-2 text-slate-400">{t.token}</code>
              </Button>
            ))}
          </div>

          {mode === "broadcast" && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="test@example.com — send a single test"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTest}
                  disabled={sendBroadcast.isPending || !subject || !html}
                >
                  {testSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Send test"
                  )}
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-amber-900">
                      Recipient preview
                    </div>
                    <div className="text-sm text-amber-800">
                      {preview
                        ? `${preview.recipientCount.toLocaleString()} recipients`
                        : "Click Preview to compute the recipient set"}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewBroadcast.isPending}
                  >
                    {previewBroadcast.isPending ? "..." : "Preview"}
                  </Button>
                </div>
                {preview && preview.sampleRecipients.length > 0 && (
                  <div className="mt-2 text-xs text-amber-900">
                    Sample:{" "}
                    {preview.sampleRecipients
                      .map((r) => r.email)
                      .slice(0, 3)
                      .join(", ")}
                    {preview.recipientCount > 3 && "..."}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            {/* Discard draft — only shown when there's actually something
                to discard. Uses an in-modal two-click confirm pattern: the
                first click arms (red + "Click again to confirm") with a
                4-second auto-disarm; a second click within that window
                performs the discard. No native browser dialog. */}
            {(subject || html.length > 50) && (
              <button
                type="button"
                onClick={() => {
                  if (discardArmed) {
                    // Second click — discard.
                    if (discardTimerRef.current !== null) {
                      window.clearTimeout(discardTimerRef.current);
                      discardTimerRef.current = null;
                    }
                    setDiscardArmed(false);
                    setSubject("");
                    setHtml(
                      "<p>Hi {{first_name}},</p>\n<p></p>\n<p>— Noah</p>",
                    );
                    setUnknownTokens([]);
                    setPreview(null);
                    notify.success("Draft discarded.");
                    return;
                  }
                  // First click — arm.
                  setDiscardArmed(true);
                  if (discardTimerRef.current !== null) {
                    window.clearTimeout(discardTimerRef.current);
                  }
                  discardTimerRef.current = window.setTimeout(() => {
                    setDiscardArmed(false);
                    discardTimerRef.current = null;
                  }, DISCARD_ARM_MS);
                }}
                className={
                  "text-xs underline transition-colors " +
                  (discardArmed
                    ? "text-red-600 font-semibold"
                    : "text-slate-500 hover:text-red-600")
                }
                aria-pressed={discardArmed}
                title={
                  discardArmed
                    ? "Click again within 4s to confirm. Otherwise this resets."
                    : "Clear the current subject and body."
                }
              >
                {discardArmed ? "Click again to confirm" : "Discard draft"}
              </button>
            )}
            <div className="flex items-center justify-end gap-2 ml-auto">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {mode === "individual" ? (
                <Button
                  onClick={handleSendIndividual}
                  disabled={sendIndividual.isPending || !subject || !html}
                  className="bg-[#B87333] hover:bg-[#A67329]"
                >
                  {sendIndividual.isPending ? "Sending..." : "Send email"}
                </Button>
              ) : (
                <Button
                  onClick={handleSendBroadcast}
                  disabled={
                    sendBroadcast.isPending || !preview || !subject || !html
                  }
                  className="bg-[#B87333] hover:bg-[#A67329]"
                >
                  {sendBroadcast.isPending
                    ? "Broadcasting..."
                    : preview
                      ? `Send to ${preview.recipientCount}`
                      : "Send broadcast"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ── Right column: AI helper ── */}
        <AiHelperPanel
          loading={aiDraft.isPending}
          activeQuickPrompt={activeQuickPrompt}
          aiPrompt={aiPrompt}
          setAiPrompt={setAiPrompt}
          aiAudience={aiAudience}
          setAiAudience={setAiAudience}
          quickPrompts={QUICK_PROMPTS}
          hasDraft={!!subject || html.length > 50}
          onDraft={(override) => handleAiDraft(override)}
          onQuickPrompt={(label, prompt) => {
            setActiveQuickPrompt(label);
            setAiPrompt(prompt);
            handleAiDraft(prompt).finally(() => setActiveQuickPrompt(null));
          }}
        />
      </div>
    </Modal>
  );
}

// ── Body editor with HTML / Preview tabs ──────────────────────────────
// The textarea stays mounted while previewing so the cursor/scroll position
// is preserved between tab switches; we just toggle which one is visible.
// Preview renders inside a sandboxed iframe so admin-pasted HTML can't run
// scripts or escape into the parent admin page.
function BodyEditor({
  html,
  onChange,
  unknownTokens = [],
}: {
  html: string;
  onChange: (next: string) => void;
  /** {{tokens}} the AI emitted that won't substitute — shown as a warning. */
  unknownTokens?: string[];
}) {
  const [view, setView] = useState<"html" | "preview">("html");

  // Substitute the merge tokens with example values so the preview shows
  // what a real recipient will see, not literal `{{first_name}}` text.
  // The actual server-side substitution uses per-recipient data; this is
  // only for the admin's visual sanity check.
  const SAMPLE = {
    first_name: "Jordan",
    last_name: "Lee",
    email: "jordan.lee@example.com",
    company: "Acme Co",
    unsubscribe_url: "#preview-unsubscribe",
  };
  const previewHtml = html
    .replace(/\{\{\s*first_name\s*\}\}/gi, SAMPLE.first_name)
    .replace(/\{\{\s*last_name\s*\}\}/gi, SAMPLE.last_name)
    .replace(/\{\{\s*email\s*\}\}/gi, SAMPLE.email)
    .replace(/\{\{\s*company\s*\}\}/gi, SAMPLE.company)
    .replace(/\{\{\s*unsubscribe_url\s*\}\}/gi, SAMPLE.unsubscribe_url);

  // Wrap the body in a minimal HTML document so the preview's typography
  // resembles what mail clients render rather than the admin shell's
  // Tailwind defaults.
  const previewDoc = `<!doctype html>
<html><head><meta charset="utf-8"><base target="_blank"><style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2937; line-height: 1.55; padding: 16px; margin: 0; background: #fff; }
  p { margin: 0 0 12px; }
  a { color: #B87333; }
  hr { border: 0; border-top: 1px solid #e5e7eb; margin: 20px 0; }
  ul, ol { margin: 0 0 12px 20px; }
  code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
</style></head><body>${previewHtml}</body></html>`;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-slate-700">
          Body (HTML)
        </label>
        <div
          role="tablist"
          aria-label="Body view"
          className="inline-flex rounded-md border border-slate-300 bg-slate-100 p-0.5 text-xs"
        >
          {(
            [
              { key: "html", label: "HTML" },
              { key: "preview", label: "Preview" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={view === tab.key}
              onClick={() => setView(tab.key)}
              className={
                "px-3 py-1 rounded font-semibold transition-colors " +
                (view === tab.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700")
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Keep the textarea mounted across tabs so cursor + scroll state
          survives toggling. CSS hides it when previewing. */}
      <textarea
        value={html}
        onChange={(e) => onChange(e.target.value)}
        rows={18}
        // Force light background + dark text so the OS dark-mode UA
        // stylesheet doesn't render this as white-on-near-black.
        className={
          "w-full rounded-md border border-slate-300 bg-white text-slate-900 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#B87333]/50 " +
          (view === "html" ? "block" : "hidden")
        }
      />

      {view === "preview" && (
        <div className="rounded-md border border-slate-300 bg-white overflow-hidden">
          {unknownTokens.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
              <strong>Heads up:</strong> the draft uses{" "}
              {unknownTokens.length === 1
                ? "a merge token"
                : `${unknownTokens.length} merge tokens`}{" "}
              the system can&apos;t substitute. Recipients will see{" "}
              {unknownTokens.map((t, i) => (
                <span key={t}>
                  <code className="bg-amber-100 px-1 rounded">{t}</code>
                  {i < unknownTokens.length - 1 ? ", " : ""}
                </span>
              ))}{" "}
              as raw text. Edit the HTML to replace{" "}
              {unknownTokens.length === 1 ? "it" : "them"} with literal values,
              or with one of: <code>{`{{first_name}}`}</code>,{" "}
              <code>{`{{last_name}}`}</code>, <code>{`{{company}}`}</code>,{" "}
              <code>{`{{email}}`}</code>, <code>{`{{unsubscribe_url}}`}</code>.
            </div>
          )}
          <iframe
            title="Email body preview"
            // sandbox without allow-scripts prevents <script> in admin
            // HTML from executing; allow-same-origin so srcDoc base
            // styles take effect.
            sandbox="allow-same-origin"
            srcDoc={previewDoc}
            className="w-full h-[460px] bg-white"
          />
          <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
            Preview substitutes merge tokens with example values
            (first_name=Jordan, company=Acme Co). Actual recipient data is used
            at send time.
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-1">
        Click a token below to insert it into the body at the end.
      </p>
    </div>
  );
}

// ── AI helper panel (right rail of the compose modal) ─────────────────
function AiHelperPanel({
  loading,
  activeQuickPrompt,
  aiPrompt,
  setAiPrompt,
  aiAudience,
  setAiAudience,
  quickPrompts,
  hasDraft,
  onDraft,
  onQuickPrompt,
}: {
  loading: boolean;
  activeQuickPrompt: string | null;
  aiPrompt: string;
  setAiPrompt: (v: string) => void;
  aiAudience: string;
  setAiAudience: (v: string) => void;
  quickPrompts: Array<{ label: string; prompt: string }>;
  hasDraft: boolean;
  onDraft: (overridePrompt?: string) => void;
  onQuickPrompt: (label: string, prompt: string) => void;
}) {
  return (
    <aside className="border border-purple-200 bg-gradient-to-b from-purple-50/70 to-white rounded-lg p-4 flex flex-col gap-3 h-fit lg:sticky lg:top-0">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-md bg-purple-100 text-purple-700">
          <Sparkles className="h-4 w-4" />
        </div>
        <h3 className="font-bold text-slate-900">AI assistant</h3>
      </div>
      <p className="text-xs text-slate-600">
        Describe what you want the email to say and the assistant will draft (or
        refine) the subject + body. Output uses merge tokens like{" "}
        <code>{`{{first_name}}`}</code> automatically.
      </p>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          What should this email say?
        </label>
        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          rows={5}
          placeholder='e.g. "Announce the launch of the AI Engineering Foundations course. Mention the early-bird $99 price ends Friday. Friendly tone."'
          className="w-full rounded-md border border-slate-300 bg-white text-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/50"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Audience notes (optional)
        </label>
        <Input
          value={aiAudience}
          onChange={(e) => setAiAudience(e.target.value)}
          placeholder="e.g. paying customers, early-access list"
          className="text-sm"
        />
      </div>

      <Button
        onClick={() => onDraft()}
        disabled={loading || !aiPrompt.trim()}
        className="bg-purple-600 hover:bg-purple-700 text-white w-full"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Drafting...
          </>
        ) : (
          <>
            <Wand2 className="h-4 w-4 mr-2" />
            {hasDraft ? "Refine draft" : "Generate draft"}
          </>
        )}
      </Button>

      <div className="border-t border-purple-200 pt-3">
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Quick prompts
        </div>
        <div className="flex flex-wrap gap-1.5">
          {quickPrompts.map((q) => {
            const isActive = activeQuickPrompt === q.label;
            return (
              <Button
                key={q.label}
                variant="outline"
                size="sm"
                // Disable all quick prompts while any AI request is in flight
                // (avoids accidental double-fire), but keep the active one
                // visually distinct so the admin sees which one is running.
                disabled={loading}
                onClick={() => onQuickPrompt(q.label, q.prompt)}
                className={
                  "text-xs border-purple-200 text-purple-700 hover:bg-purple-50 " +
                  (isActive ? "bg-purple-100 border-purple-400" : "")
                }
              >
                {isActive && loading ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    Refining…
                  </>
                ) : (
                  q.label
                )}
              </Button>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-1">
        Drafts are suggestions — always review before sending.
      </p>
    </aside>
  );
}

// ── Date helpers ────────────────────────────────────────────────────────
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
